import { useEffect, useRef, useState } from 'react';
import { useMapsLibrary, APIProvider } from '@vis.gl/react-google-maps';
import { MapPin, Loader2 } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Inner component that renders the input + autocomplete list, using the loaded Places library
const AutocompleteInput = ({ value, onChange, onSelect, placeholder, testid }) => {
  const places = useMapsLibrary('places');
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (places && !sessionToken) {
      setSessionToken(new places.AutocompleteSessionToken());
    }
  }, [places, sessionToken]);

  useEffect(() => {
    const onClickOut = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  const search = async (q) => {
    if (!places || !q || q.length < 3) {
      setPredictions([]);
      return;
    }
    setLoading(true);
    try {
      // Prefer new Places API (available to all Google Cloud customers)
      if (places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: q,
          sessionToken,
          includedRegionCodes: ['za'],
          language: 'en',
        });
        setPredictions(
          (suggestions || []).map((s) => ({
            place_id: s.placePrediction?.placeId,
            _prediction: s.placePrediction,
            description: s.placePrediction?.text?.text,
            structured_formatting: {
              main_text: s.placePrediction?.mainText?.text,
              secondary_text: s.placePrediction?.secondaryText?.text,
            },
          })).filter((p) => p.place_id)
        );
      } else if (places.AutocompleteService) {
        // Fallback to legacy AutocompleteService
        const service = new places.AutocompleteService();
        service.getPlacePredictions(
          {
            input: q,
            sessionToken,
            componentRestrictions: { country: 'za' },
            types: ['address'],
          },
          (results) => setPredictions(results || [])
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Places autocomplete failed:', e?.message || e);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const onInput = (e) => {
    const v = e.target.value;
    onChange(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const pick = async (prediction) => {
    if (!places) return;
    try {
      // New API path
      if (prediction._prediction?.toPlace) {
        const place = prediction._prediction.toPlace();
        await place.fetchFields({ fields: ['formattedAddress', 'location'] });
        const loc = place.location;
        onSelect({
          formatted_address: place.formattedAddress || prediction.description,
          lat: loc ? loc.lat() : null,
          lng: loc ? loc.lng() : null,
        });
        setPredictions([]);
        setOpen(false);
        setSessionToken(new places.AutocompleteSessionToken());
        return;
      }
      // Legacy fallback
      const svc = new places.PlacesService(document.createElement('div'));
      svc.getDetails(
        { placeId: prediction.place_id, fields: ['formatted_address', 'geometry'] },
        (place, status) => {
          if (status !== 'OK' || !place) return;
          const loc = place.geometry?.location;
          onSelect({
            formatted_address: place.formatted_address || prediction.description,
            lat: loc ? loc.lat() : null,
            lng: loc ? loc.lng() : null,
          });
          setPredictions([]);
          setOpen(false);
          setSessionToken(new places.AutocompleteSessionToken());
        }
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch place details:', e?.message || e);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" strokeWidth={1.5} />
        <input
          type="text"
          value={value || ''}
          onChange={onInput}
          onFocus={() => setOpen(predictions.length > 0)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 border border-gray-400 bg-paper rounded-none focus:outline-none focus:ring-2 focus:ring-ink/30 text-sm"
          data-testid={testid}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted animate-spin" strokeWidth={1.5} />}
      </div>
      {open && predictions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-paper border border-gray-400 shadow-lg max-h-72 overflow-y-auto"
          data-testid="address-suggestions"
        >
          {predictions.map((p) => (
            <li
              key={p.place_id}
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              className="px-3 py-2.5 hover:bg-ink hover:text-paper cursor-pointer border-b border-line last:border-b-0 flex items-start gap-2"
              data-testid={`address-suggestion-${p.place_id}`}
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{p.structured_formatting?.main_text || p.description}</div>
                <div className="text-xs text-muted truncate group-hover:text-paper/70">
                  {p.structured_formatting?.secondary_text}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AddressAutocomplete = (props) => {
  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_GOOGLE_MAPS_API_KEY') {
    // Fallback: plain input if Maps key is not set
    return (
      <input
        type="text"
        value={props.value || ''}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="w-full px-3 py-2 border border-gray-400 bg-paper rounded-none focus:outline-none focus:ring-2 focus:ring-ink/30 text-sm"
        data-testid={props.testid}
      />
    );
  }
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <AutocompleteInput {...props} />
    </APIProvider>
  );
};

export default AddressAutocomplete;
